import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { ProdutoCaixa, PromocaoGrupoCaixa } from '@caixa/lib/api';
import { formatBRL } from '@caixa/lib/format';

export type ItemEscolhidoPromo = {
  produto_id: number;
  nome_produto: string;
  quantidade: number;
  preco_unitario: number;
};

type Props = {
  open: boolean;
  produto: ProdutoCaixa | null;
  onClose: () => void;
  onConfirm: (itens: ItemEscolhidoPromo[]) => void;
};

/** Por grupo: índice do produto escolhido no array produtos do grupo (-1 = nenhum) */
function getDefaultSelecoes(grupos: PromocaoGrupoCaixa[]): number[] {
  return grupos.map(() => -1);
}

export function PromoConfiguravelModal({ open, produto, onClose, onConfirm }: Props) {
  const grupos = produto?.promocao_grupos ?? [];
  const [selecoes, setSelecoes] = useState<number[]>(() => getDefaultSelecoes(grupos));

  useEffect(() => {
    if (open) {
      setSelecoes(getDefaultSelecoes(grupos));
    }
  }, [open, grupos]);

  const todosPreenchidos = grupos.length > 0 && grupos.every((g, i) => {
    const qty = g.quantidade_obrigatoria ?? 1;
    const sel = selecoes[i] ?? -1;
    return qty <= 1 ? sel >= 0 : sel >= 0; // por enquanto 1 por grupo
  });

  const handleConfirm = () => {
    if (!produto || !todosPreenchidos) return;
    const itens: ItemEscolhidoPromo[] = [];
    grupos.forEach((g, gi) => {
      const idx = selecoes[gi];
      if (idx >= 0 && g.produtos[idx]) {
        const p = g.produtos[idx];
        itens.push({
          produto_id: p.produto_id,
          nome_produto: p.produto_nome ?? '',
          quantidade: 1,
          preco_unitario: 0,
        });
      }
    });
    onConfirm(itens);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="caixa-overlay">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-config-title"
        className="caixa-modal caixa-modal--md"
      >
        <div className="caixa-modal-head">
          <h2 id="promo-config-title" className="caixa-modal-title">
            {produto?.nome ?? 'Promoção'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="caixa-modal-close"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {produto && (
          <p className="text-amber-400 font-medium mb-4">{formatBRL(produto.preco_venda ?? 0)}</p>
        )}
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {grupos.map((g, gi) => (
            <div key={gi} className="border border-[#3a3a3a] rounded-lg p-3">
              <p className="text-sm font-medium text-[#a3a3a3] mb-2">
                Escolha {g.quantidade_obrigatoria} item(ns) do grupo: {g.nome_grupo}
              </p>
              <div className="space-y-1.5">
                {g.produtos.map((p, pi) => (
                  <label
                    key={p.produto_id}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer ${selecoes[gi] === pi ? 'bg-amber-500/20 border border-amber-500/50' : 'hover:bg-[#333333]/50 border border-transparent'}`}
                  >
                    <input
                      type="radio"
                      name={`grupo-${gi}`}
                      checked={selecoes[gi] === pi}
                      onChange={() => setSelecoes((prev) => prev.map((s, i) => (i === gi ? pi : s)))}
                      className="sr-only"
                    />
                    <span className="text-[#f5f5f5]">{p.produto_nome}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="caixa-modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="caixa-btn-outline"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!todosPreenchidos}
            className="caixa-btn-success"
          >
            Adicionar ao carrinho
          </button>
        </div>
      </div>
    </div>
  );
}
